import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const suggestionItems = [
  {
    key: 'services',
    icon: 'ri-service-line',
    questionEn: 'What mentoring services do you offer and how do they work?',
    questionEs: '¿Qué servicios de mentoría ofrecen y cómo funcionan?',
  },
  {
    key: 'career',
    icon: 'ri-briefcase-line',
    questionEn: 'I work in supply chain — how can you help me advance my career?',
    questionEs: 'Trabajo en la cadena de suministro — ¿cómo pueden ayudarme a avanzar en mi carrera?',
  },
  {
    key: 'discovery',
    icon: 'ri-calendar-check-line',
    questionEn: 'What happens during a discovery call and how do I prepare?',
    questionEs: '¿Qué sucede durante una llamada de descubrimiento y cómo me preparo?',
  },
];

export default function ChatSuggestions() {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);

  const isSpanish = i18n.language?.startsWith('es');

  // Wait for widget to be in the DOM
  useEffect(() => {
    const checkWidget = () => {
      const btn = document.querySelector('#vapi-widget-floating-button');
      if (btn) {
        setWidgetReady(true);
      } else {
        setTimeout(checkWidget, 500);
      }
    };
    checkWidget();
  }, []);

  // Listen for widget open/close via click on the widget button
  useEffect(() => {
    if (!widgetReady) return;

    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (!btn) return;

    const handleClick = () => {
      // Toggle visibility — slight delay so the widget has time to render
      setTimeout(() => {
        setVisible((prev) => !prev);
      }, 300);
    };

    // Also listen for clicks outside the widget to hide suggestions
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('#vapi-widget-floating-button') &&
        !target.closest('[data-chat-suggestions]') &&
        !target.closest('.vapi-widget')
      ) {
        // Don't immediately hide — the widget might still be opening
        setTimeout(() => {
          const widgetOpen = document.querySelector('.vapi-widget[data-open="true"]') || 
                             document.querySelector('.vapi-widget:not([style*="display: none"])');
          if (!widgetOpen) {
            setVisible(false);
          }
        }, 500);
      }
    };

    btn.addEventListener('click', handleClick);
    document.addEventListener('click', handleOutsideClick);

    return () => {
      btn.removeEventListener('click', handleClick);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [widgetReady]);

  const handleSuggestionClick = useCallback((questionEn: string, questionEs: string) => {
    const question = isSpanish ? questionEs : questionEn;

    // Open the widget if not already open
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (btn) {
      btn.click();
    }

    // Try to find the chat input and set the message
    setTimeout(() => {
      // Vapi widget uses a textarea or contenteditable div for input
      const textarea = document.querySelector('.vapi-widget textarea') as HTMLTextAreaElement;
      const inputDiv = document.querySelector('.vapi-widget [contenteditable="true"]') as HTMLElement;
      const anyInput = document.querySelector('.vapi-widget [role="textbox"]') as HTMLElement;

      const input = textarea || inputDiv || anyInput;

      if (input) {
        if (input instanceof HTMLTextAreaElement) {
          input.value = question;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          input.textContent = question;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        input.focus();

        // Try to click the send button
        setTimeout(() => {
          const sendBtn = document.querySelector('.vapi-widget button[type="submit"]') as HTMLElement;
          const sendIcon = document.querySelector('.vapi-widget [data-send]') as HTMLElement;
          const anySend = document.querySelector('.vapi-widget button:has(svg)') as HTMLElement;
          (sendBtn || sendIcon || anySend)?.click();
        }, 200);

        setVisible(false);
      }
    }, 600);
  }, [isSpanish]);

  if (!widgetReady) return null;

  return (
    <div
      data-chat-suggestions
      className={`fixed bottom-24 right-5 z-[9998] flex flex-col gap-2 transition-all duration-300 ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <p className="text-xs text-foreground-500 font-medium px-1 mb-1">
        {isSpanish ? 'Pregúntale a Hodos:' : 'Ask Hodos:'}
      </p>
      {suggestionItems.map((item) => (
        <button
          key={item.key}
          onClick={() => handleSuggestionClick(item.questionEn, item.questionEs)}
          className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-background-200 rounded-xl 
                     text-sm text-foreground-800 hover:bg-background-50 hover:border-accent-300
                     transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md
                     text-left whitespace-nowrap max-w-[320px] group"
        >
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 group-hover:bg-accent-200 transition-colors text-xs">
            <i className={item.icon}></i>
          </span>
          <span className="truncate">
            {isSpanish ? item.questionEs : item.questionEn}
          </span>
        </button>
      ))}
    </div>
  );
}